import {
  Box,
  type BoxProps,
  IconButton,
  TextField,
  Typography,
} from '@mui/material';
import { alpha, styled } from '@mui/material/styles';
import { Chat, Close, Download, Send } from '@mui/icons-material';
import { format } from 'date-fns';
import { useState, useRef, useEffect, useMemo } from 'react';
import type { MatterViewModel } from '~/modules/matters/matters.types';
import type { Message, User } from '~/shared/types/connect';
import { getMatterDisplayHeading } from '~/shared/utils/matterDisplay';
import { AvatarImage } from '~/shared/components/AvatarImage';

interface UserLookup {
  id: string;
  name: string;
  avatarUrl?: string;
}

interface Props {
  open: boolean;
  onClose: () => void;
  expert: User;
  matter: MatterViewModel | null;
  currentUserId: string;
  currentUserEmail?: string;
  messages: Message[];
  onSendMessage: (content: string) => void;
  onDownloadHistory?: () => void;
  getUserById?: (id: string) => User | UserLookup;
}

const SIDEBAR_WIDTH = 400;
const MESSAGE_TIMESTAMP_FORMAT = 'MMM d, h:mm a';

const ChatSidebar = ({
  open,
  onClose,
  expert,
  matter,
  currentUserId,
  currentUserEmail,
  messages,
  onSendMessage,
  onDownloadHistory,
}: Props) => {
  const [input, setInput] = useState('');
  const bottomRef = useRef<HTMLDivElement>(null);
  const chatInputRef = useRef<HTMLInputElement | HTMLTextAreaElement>(null);

  const matterMessages = useMemo(() => {
    if (!matter) {
      return [];
    }
    return messages
      .filter((message) => message.matterId === matter.id)
      .sort(
        (a, b) =>
          new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
      );
  }, [matter, messages]);

  useEffect(() => {
    if (open) {
      bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [open, matterMessages.length]);

  useEffect(() => {
    if (open) {
      const focusInput = () => chatInputRef.current?.focus();
      const id = requestAnimationFrame(() => {
        requestAnimationFrame(focusInput);
      });
      return () => cancelAnimationFrame(id);
    }
  }, [open]);

  useEffect(() => {
    if (!open) {
      return;
    }
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' || event.keyCode === 27) {
        onClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [open, onClose]);

  const handleSend = () => {
    const trimmed = input.trim();
    if (!trimmed || !matter) {
      return;
    }
    onSendMessage(trimmed);
    setInput('');
  };

  if (!open) {
    return null;
  }

  return (
    <>
      <Backdrop onClick={onClose} aria-hidden />
      <SidebarPanel
        component="aside"
        role="dialog"
        aria-modal="true"
        aria-label={`Chat with ${expert.name}`}
        $open={open}
      >
        <HeaderRow>
          <ChatIcon aria-hidden />
          <HeaderTitle variant="subtitle1" fontWeight={600}>
            {expert.name}
          </HeaderTitle>
          <HistoryDownloadLink
            type="button"
            onClick={() => onDownloadHistory?.()}
            disabled={!onDownloadHistory}
          >
            <DownloadIcon aria-hidden />
            History
          </HistoryDownloadLink>
          <CloseIconButton
            onClick={onClose}
            aria-label="Close chat"
            size="small"
          >
            <CloseIcon aria-hidden />
          </CloseIconButton>
        </HeaderRow>

        <MessagesScroll>
          {!matter && (
            <EmptyStateBox>
              <Typography variant="body2">
                Create a matter with this expert to start messaging.
              </Typography>
            </EmptyStateBox>
          )}
          {matter && matterMessages.length === 0 && (
            <EmptyStateBox>
              <Typography variant="body2">
                No messages yet. Send a message to start the conversation.
              </Typography>
            </EmptyStateBox>
          )}
          {matter &&
            matterMessages.length > 0 &&
            matterMessages.map((msg) => {
              const isCurrentUser = currentUserEmail
                ? msg.senderEmail === currentUserEmail
                : msg.senderId === currentUserId;
              const senderName = msg.senderName ?? 'Unknown';
              return (
                <MessageRow key={msg.id} $isCurrentUser={isCurrentUser}>
                  <MessageBubbleRow $isCurrentUser={isCurrentUser}>
                    <MessageAvatar
                      avatarUrl={msg.senderAvatarUrl}
                      initialsLabel={senderName}
                      alt={senderName}
                    />
                    <MessageBubbleColumn $isCurrentUser={isCurrentUser}>
                      <MatterCaption variant="caption">
                        RE: {getMatterDisplayHeading(matter)}
                      </MatterCaption>
                      <MessageBubble $isCurrentUser={isCurrentUser}>
                        <MessageContent variant="body2">
                          {msg.content}
                        </MessageContent>
                      </MessageBubble>
                    </MessageBubbleColumn>
                  </MessageBubbleRow>
                  <TimestampCaption variant="caption" color="text.secondary">
                    {format(new Date(msg.createdAt), MESSAGE_TIMESTAMP_FORMAT)}
                  </TimestampCaption>
                </MessageRow>
              );
            })}
          <div ref={bottomRef} />
        </MessagesScroll>

        {matter && (
          <InputRow>
            <StyledTextField
              fullWidth
              multiline
              maxRows={3}
              placeholder="Type a message..."
              value={input}
              inputRef={chatInputRef}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleSend();
                }
              }}
              size="small"
            />
            <SendButton
              onClick={handleSend}
              disabled={!input.trim()}
              aria-label="Send message"
            >
              <SendIcon aria-hidden />
            </SendButton>
          </InputRow>
        )}
      </SidebarPanel>
    </>
  );
};

const Backdrop = styled(Box)(({ theme }) => ({
  position: 'fixed',
  inset: 0,
  backgroundColor: alpha(theme.palette.common.black, 0.2),
  zIndex: theme.zIndex.drawer,
  opacity: 1,
  visibility: 'visible',
  transition: 'opacity 0.2s ease, visibility 0.2s ease',
}));

const SidebarPanel = styled(Box, {
  shouldForwardProp: (prop) => prop !== '$open',
})<BoxProps & { $open: boolean }>(({ theme, $open }) => ({
  position: 'fixed',
  top: 0,
  right: 0,
  width: '100%',
  maxWidth: '100vw',
  height: '100vh',
  backgroundColor: theme.palette.background.paper,
  boxShadow: theme.shadows[8],
  borderTopLeftRadius: Number(theme.shape.borderRadius) * 3,
  borderBottomLeftRadius: Number(theme.shape.borderRadius) * 3,
  zIndex: theme.zIndex.drawer + 1,
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
  transform: $open ? 'translateX(0)' : 'translateX(100%)',
  transition: 'transform 0.25s ease',
  [theme.breakpoints.up('sm')]: {
    width: SIDEBAR_WIDTH,
  },
}));

const HeaderRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1.5),
  paddingLeft: theme.spacing(2),
  paddingRight: theme.spacing(2),
  paddingTop: theme.spacing(1.5),
  paddingBottom: theme.spacing(1.5),
  borderBottom: `1px solid ${theme.palette.divider}`,
  flexShrink: 0,
  boxShadow: theme.shadows[1],
}));

const ChatIcon = styled(Chat)(({ theme }) => ({
  fontSize: 22,
  color: theme.palette.darkNavy,
}));

const HeaderTitle = styled(Typography)({
  flex: 1,
});

const DownloadIcon = styled(Download)(({ theme }) => ({
  fontSize: 16,
  marginRight: theme.spacing(0.5),
  verticalAlign: 'middle',
}));

const HistoryDownloadLink = styled('button')(({ theme }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  background: 'none',
  border: 'none',
  font: 'inherit',
  ...theme.typography.body2,
  color: theme.palette.primary.main,
  textDecoration: 'none',
  cursor: 'pointer',
  paddingLeft: theme.spacing(1),
  paddingRight: theme.spacing(1),
  '&:hover': {
    textDecoration: 'underline',
  },
  '&:focus-visible': {
    outline: `2px solid ${theme.palette.primary.main}`,
    outlineOffset: 2,
  },
}));

const CloseIconButton = styled(IconButton)(({ theme }) => ({
  color: theme.palette.text.secondary,
}));

const CloseIcon = styled(Close)({
  fontSize: 20,
});

const MessagesScroll = styled(Box)(({ theme }) => ({
  flex: 1,
  overflowY: 'auto',
  overflowX: 'hidden',
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(2),
  paddingTop: theme.spacing(2),
  paddingRight: theme.spacing(2),
  paddingBottom: theme.spacing(2),
  paddingLeft: theme.spacing(2),
  '@media (hover: hover)': {
    '&::-webkit-scrollbar': { width: 6 },
    '&::-webkit-scrollbar-track': { background: 'transparent' },
    '&::-webkit-scrollbar-thumb': {
      background: theme.palette.grey[400],
      borderRadius: theme.shape.borderRadius,
    },
  },
}));

const EmptyStateBox = styled(Box)(({ theme }) => ({
  paddingTop: theme.spacing(4),
  paddingBottom: theme.spacing(4),
  textAlign: 'center',
  color: theme.palette.text.secondary,
}));

const MessageRow = styled(Box, {
  shouldForwardProp: (prop) => prop !== '$isCurrentUser',
})<{ $isCurrentUser: boolean }>(({ theme, $isCurrentUser }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: $isCurrentUser ? 'flex-end' : 'flex-start',
  width: $isCurrentUser ? '100%' : undefined,
  maxWidth: $isCurrentUser ? '100%' : '85%',
  gap: theme.spacing(0.5),
}));

const MessageBubbleRow = styled(Box, {
  shouldForwardProp: (prop) => prop !== '$isCurrentUser',
})<{ $isCurrentUser: boolean }>(({ theme, $isCurrentUser }) => ({
  display: 'flex',
  flexDirection: $isCurrentUser ? 'row-reverse' : 'row',
  alignItems: 'flex-end',
  gap: theme.spacing(1),
}));

const MessageAvatar = styled(AvatarImage)(({ theme }) => ({
  width: 32,
  height: 32,
  flexShrink: 0,
  fontSize: theme.typography.caption.fontSize,
}));

const MessageBubbleColumn = styled(Box, {
  shouldForwardProp: (prop) => prop !== '$isCurrentUser',
})<{ $isCurrentUser: boolean }>(({ $isCurrentUser }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: $isCurrentUser ? 'flex-end' : 'flex-start',
}));

const MatterCaption = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.secondary,
  marginBottom: theme.spacing(0.25),
  paddingLeft: theme.spacing(0.5),
  paddingRight: theme.spacing(0.5),
}));

const MessageBubble = styled(Box, {
  shouldForwardProp: (prop) => prop !== '$isCurrentUser',
})<{ $isCurrentUser: boolean }>(({ theme, $isCurrentUser }) => ({
  paddingLeft: theme.spacing(1.5),
  paddingRight: theme.spacing(1.5),
  paddingTop: theme.spacing(1),
  paddingBottom: theme.spacing(1),
  borderRadius: Number(theme.shape.borderRadius) * 2,
  ...($isCurrentUser
    ? {
        backgroundColor: theme.palette.grey[600],
        color: theme.palette.common.white,
        borderTopRightRadius: theme.shape.borderRadius,
      }
    : {
        backgroundColor: theme.palette.grey[200],
        color: theme.palette.text.primary,
        borderTopLeftRadius: theme.shape.borderRadius,
      }),
}));

const MessageContent = styled(Typography)({
  whiteSpace: 'pre-wrap',
});

const TimestampCaption = styled(Typography)(({ theme }) => ({
  marginBottom: theme.spacing(0.5),
  paddingLeft: theme.spacing(0.5),
  paddingRight: theme.spacing(0.5),
}));

const InputRow = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
  borderTop: `1px solid ${theme.palette.divider}`,
  flexShrink: 0,
  display: 'flex',
  gap: theme.spacing(1),
  alignItems: 'flex-end',
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: Number(theme.shape.borderRadius) * 2,
    backgroundColor: theme.palette.grey[50],
  },
}));

const SendButton = styled(IconButton)(({ theme }) => ({
  backgroundColor: theme.palette.teal?.main,
  color: theme.palette.darkNavy,
  width: 44,
  height: 44,
  flexShrink: 0,
  '&:hover': {
    backgroundColor: theme.palette.teal?.dark,
  },
  '&.Mui-disabled': {
    backgroundColor: theme.palette.grey[300],
    color: theme.palette.grey[500],
  },
}));

const SendIcon = styled(Send)({
  fontSize: 20,
});

export default ChatSidebar;
